// src/components/cities/CitiesTable.jsx
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { Stack, useTheme } from "@mui/material";

import useGetCities from "../../hooks/cities/useGetCities";
import useDeleteCity from "../../hooks/cities/useDeleteCity";
import useDeleteAllCities from "../../hooks/cities/useDeleteAllCities";
import useRestoreCity from "../../hooks/cities/useRestoreCity";
import useRestoreAllCities from "../../hooks/cities/useRestoreAllCities";
import useForceDeleteCity from "../../hooks/cities/useForceDeleteCity";
import useForceDeleteAllCities from "../../hooks/cities/useForceDeleteAllCities";
import useGetCountriesForSelect from "../../hooks/countries/useGetCountriesForSelect";

import useTableActions from "../../hooks/common/useTableActions";
import TableLayout from "../global/Table/TableLayout";
import CitiesTableRow from "./Table/CitiesTableRow";
import CitiesTableColumns from "./Table/CitiesTableColumns";
import ArchiveButton from "../global/Table/ArchiveButton";
import toast from "react-hot-toast";
import { usePermissions } from "../../auth/PermissionsContext";

const CitiesTable = () => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng === "ar";
    const { has } = usePermissions();

    const perms = useMemo(
        () => ({
            list: has("cities_list"),
            create: has("cities_create"),
            edit: has("cities_edit"),
            delete: has("cities_delete"),
            forceDelete: has("cities_force_delete"),
            archiveList: has("cities_archive_list"),
            restore: has("cities_restore"),
            restoreAll: has("cities_restore-all-table"),
            deleteAll: has("cities_delete-all-table"),
            massForceDeleteAll: has("cities_mass_force_delete-all-table"),
        }),
        [has]
    );

    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    const isArchive = location.pathname.includes("/cities/archive");

    // CRUD Hooks
    const { deleteCityMutate: deleteCity, isPending: isDelete } = useDeleteCity();
    const { deleteAllCitiesMutate: deleteAllCities, isPending: isDeleteAll } = useDeleteAllCities();
    const { restoreCityMutate:restoreCity, isPending: isRestore } = useRestoreCity();
    const { restoreAllCitiesMutate:restoreAllCities, isPending: isRestoreAll } = useRestoreAllCities();
    const { forceDeleteCityMutate: forceDeleteCity, isPending: isForceDelete } = useForceDeleteCity();
    const { forceDeleteAllCitiesMutate: forceDeleteAllCities, isPending: isForceDeleteAll } = useForceDeleteAllCities();

    const {
        page,
        perPage,
        search,
        sortBy,
        sortDir,
        selectedIds,
        menuAnchorEl,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleEdit,
        handleMenuDelete,
        handleMenuRestore,
        handleBulkDelete,
        handlePageChange,
        handlePerPageChange,
        setSelectedIds,
    } = useTableActions({
        basePath: "/cities",
        deleteSingle: deleteCity,
        deleteAll: deleteAllCities,
        restoreSingle: restoreCity,
        restoreAll: restoreAllCities,
        forceDeleteSingle: forceDeleteCity,
        forceDeleteAll: forceDeleteAllCities,
        isArchive,
    });

    const [statusFilter, setStatusFilter] = React.useState("");
    const [countryFilter, setCountryFilter] = React.useState("");

    const { options: countryOptions } = useGetCountriesForSelect();

    const filters = useMemo(
        () => ({
            search: search || undefined,
            status: statusFilter || undefined,
            country_id: countryFilter || undefined,
            sort_by: sortBy,
            sort_dir: sortDir,
            ...(isArchive ? { only_trashed: true } : {}),
        }),
        [search, statusFilter, countryFilter, sortBy, sortDir, isArchive]
    );

    const filterConfig = useMemo(
        () => [
            {
                type: "select",
                name: "country_id",
                label: { ar: "الدولة", en: "Country" },
                options: [
                    { value: "", label: { ar: "الكل", en: "All" } },
                    ...countryOptions.map((opt) => ({
                        value: opt.value,
                        label: { ar: opt.label, en: opt.label },
                    })),
                ],
                value: countryFilter,
                onChange: setCountryFilter,
            },
            {
                type: "select",
                name: "status",
                label: { ar: "الحالة", en: "Status" },
                options: [
                    { value: "", label: { ar: "الكل", en: "All" } },
                    { value: "active", label: { ar: "نشط", en: "Active" } },
                    { value: "disabled", label: { ar: "معطل", en: "Disabled" } },
                ],
                value: statusFilter,
                onChange: setStatusFilter,
            },
        ],
        [statusFilter, countryFilter, countryOptions]
    );

    const handleClearAllFilters = () => {
        setStatusFilter("");
        setCountryFilter("");
    };

    const { isLoading, error, cities, meta } = useGetCities(
        page,
        perPage,
        filters
    );

    const items = useMemo(() => cities?.items || [], [cities]);

    const rowMenuDisabled = isArchive
        ? !perms.restore && !perms.forceDelete
        : !perms.edit && !perms.delete;

    const renderRow = (params) => (
        <CitiesTableRow
            key={params.item.id}
            city={params.item}
            index={params.index}
            isDark={isDark}
            page={params.page}
            perPage={params.perPage}
            sortBy={params.sortBy}
            sortDir={params.sortDir}
            totalItems={items.length}
            selectedIds={selectedIds}
            onRowCheckboxClick={handleRowCheckboxClick}
            onMenuOpen={handleMenuOpen}
            isArchive={isArchive}
            menuDisabled={rowMenuDisabled}
        />
    );

    const bulkDeleteDisabled = isArchive
        ? !perms.massForceDeleteAll
        : !perms.deleteAll;

    const title = isArabic ? "المدن" : "Cities";
    const subtitle = isArchive
        ? isArabic
            ? "عرض المدن المحذوفة (الأرشيف)"
            : "Archived cities (soft deleted)"
        : isArabic
            ? "إدارة المدن المتاحة في النظام"
            : "Manage available cities in the system";

    const addButtonDisabled = isArchive
        ? !perms.restoreAll || selectedIds.length === 0
        : !perms.create;

    const addButtonLabel = isArchive
        ? isRestoreAll
            ? isArabic
                ? "جاري الاستعادة..."
                : "Restoring..."
            : isArabic
                ? "استعادة المحدد"
                : "Restore selected"
        : isArabic
            ? "إضافة مدينة"
            : "Create new city";

    const addButtonAction = isArchive
        ? () => {
            if (addButtonDisabled) {
                toast.error(
                    isArabic
                        ? "ليس لديك صلاحية للاستعادة"
                        : "No restore permission"
                );
                return;
            }

            if (isRestoreAll) return;

            if (!selectedIds.length) {
                toast.error(
                    isArabic
                        ? "الرجاء تحديد العناصر المراد استرجاعها أولاً"
                        : "Please select items to restore first"
                );
                return;
            }

            restoreAllCities({ items: selectedIds });
            setSelectedIds([]);
        }
        : () => {
            if (addButtonDisabled) {
                toast.error(
                    isArabic
                        ? "ليس لديك صلاحية للإنشاء"
                        : "No create permission"
                );
                return;
            }

            navigate("/cities/create");
        };

    return (
        <>
            {!isArchive && (
                <Stack
                    direction="row"
                    justifyContent={isArabic ? "flex-start" : "flex-end"}
                    sx={{ mb: 1 }}
                >
                    <ArchiveButton
                        to="/cities/archive"
                        visible={perms.archiveList}
                        disabled={!perms.archiveList}
                        archiveLabelAr="الأرشيف"
                        archiveLabelEn="Archive"
                    />
                </Stack>
            )}
            {isArchive && (
                <Stack
                    direction="row"
                    justifyContent={isArabic ? "flex-start" : "flex-end"}
                    sx={{ mb: 1 }}
                >
                    <ArchiveButton
                        to="/cities"
                        visible={true}
                        archiveLabelAr="العودة للقائمة"
                        archiveLabelEn="Back to list"
                    />
                </Stack>
            )}
            <TableLayout
                title={title}
                subtitle={subtitle}
                search={search}
                onSearchChange={setSearch}
                addButtonLabel={addButtonLabel}
                onAddClick={addButtonAction}
                addButtonVisible={true}
                isLoading={isLoading}
                error={error}
                items={items}
                meta={meta}
                page={page}
                perPage={perPage}
                onPageChange={handlePageChange}
                onPerPageChange={handlePerPageChange}
                sortBy={sortBy}
                sortDir={sortDir}
                onRequestSort={handleRequestSort}
                columns={CitiesTableColumns}
                renderRow={renderRow}
                menuAnchorEl={menuAnchorEl}
                openMenu={Boolean(menuAnchorEl)}
                onMenuOpen={handleMenuOpen}
                onMenuClose={handleMenuClose}
                onMenuDelete={handleMenuDelete}
                onMenuRestore={isArchive ? handleMenuRestore : undefined}
                onMenuEdit={!isArchive ? handleEdit : undefined}
                menuDisableEdit={!isArchive && !perms.edit}
                menuDisableRestore={isArchive && !perms.restore}
                menuDisableDelete={isArchive ? !perms.forceDelete : !perms.delete}
                isDeletingSingle={isArchive ? isRestore || isForceDelete : isDelete}
                selectedIds={selectedIds}
                onSelectAllClick={(e) => handleSelectAllClick(e, items)}
                onRowCheckboxClick={handleRowCheckboxClick}
                onBulkDelete={handleBulkDelete}
                bulkDeleteDisabled={bulkDeleteDisabled}
                isBulkDeleting={isArchive ? isForceDeleteAll : isDeleteAll}
                addButtonDisabled={addButtonDisabled}
                addButtonTooltip={
                    addButtonDisabled
                        ? isArabic
                            ? "ليس لديك صلاحية"
                            : "No permission"
                        : ""
                }
                filters={filterConfig}
                onClearAllFilters={handleClearAllFilters}
            />
        </>
    );
};

export default CitiesTable;